import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Send, Loader2, Clock, CheckCircle2, Building, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Stagger, StaggerItem } from "../motion";
import { OFFICES, EMAILS, SERVICES } from "../../data/content";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "", dpdpConsent: false };
const INPUT_CLASS = "w-full bg-white border border-[#811F5D]/20 rounded-xl px-4 py-3.5 text-sm text-[#2A0E20] placeholder:text-[#6B5A64]/60 focus:outline-none focus:border-[#811F5D] focus:ring-2 focus:ring-[#D4AF37]/50 shadow-sm transition-all duration-200";

import { dispatchFrontendInquiryEmails } from "../../services/emailService";

const GOOGLE_SHEET_WEBHOOK = "https://script.google.com/macros/s/AKfycbx8XT3mPhK9dl4UnO1nf2fjMW2_jEmSDrf8D7rRq0wKcqk3fox_HzOMuo973e0CoOiH/exec";

const useInquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const updateField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const updateChecked = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.checked }));
  
  const submit = async (event) => {
    event.preventDefault();
    if (!form.dpdpConsent) {
      toast.error("Please confirm DPDP consent to proceed.");
      return;
    }
    setSending(true);
    let submitted = false;

    // 1. Backend API (FastAPI with Hostinger SMTP dispatch)
    try {
      const response = await fetch(`${API || "http://localhost:8000"}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        submitted = true;
      }
    } catch (err) {
      console.warn("Backend API unavailable:", err);
    }

    // 2. Pure frontend direct email dispatch using EmailJS (if configured)
    if (!submitted) {
      try {
        const emailSent = await dispatchFrontendInquiryEmails(form);
        if (emailSent) {
          submitted = true;
        }
      } catch (err) {
        console.warn("Frontend EmailJS dispatch:", err);
      }
    }

    // 3. Direct frontend webhook sync to Google Sheets
    try {
      await fetch(GOOGLE_SHEET_WEBHOOK, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          timestamp: new Date().toISOString(),
          source: "Vardhamaan Website Contact Form"
        }),
      });
      submitted = true;
    } catch (err) {
      console.error("Direct webhook submission:", err);
    }

    if (submitted) {
      toast.success("Inquiry received — our team will respond within 1 working day.");
      setForm(EMPTY);
    } else {
      toast.error("Could not send automatically. Please email amol@vardhamaanconsultancyservices.com directly.");
    }
    setSending(false);
  };
  return { form, sending, submit, updateField, updateChecked };
};

const InquiryForm = () => {
  const { form, sending, submit, updateField, updateChecked } = useInquiryForm();
  return (
    <FadeIn className="bg-white p-8 sm:p-10 rounded-3xl border border-[#811F5D]/10 shadow-xl">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-3">Business Inquiry</p>
      <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl tracking-tight mb-2">Share your hiring requirement.</h2>
      <p className="text-sm text-[#6B5A64] mb-8">Our workforce advisors review requirements and revert within 24 hours.</p>
      
      <form onSubmit={submit} className="space-y-5" data-testid="inquiry-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-[#2A0E20] mb-1.5">Full Name *</label>
            <input required value={form.name} onChange={updateField("name")} placeholder="e.g. John Doe" className={INPUT_CLASS} data-testid="inquiry-name-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#2A0E20] mb-1.5">Company Name</label>
            <input value={form.company} onChange={updateField("company")} placeholder="e.g. Al-Mansoor Contracting" className={INPUT_CLASS} data-testid="inquiry-company-input" />
          </div>
        </div>
        
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-[#2A0E20] mb-1.5">Email Address *</label>
            <input required type="email" value={form.email} onChange={updateField("email")} placeholder="e.g. procurement@company.com" className={INPUT_CLASS} data-testid="inquiry-email-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#2A0E20] mb-1.5">Phone Number</label>
            <input value={form.phone} onChange={updateField("phone")} placeholder="e.g. +974 5562 1384" className={INPUT_CLASS} data-testid="inquiry-phone-input" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#2A0E20] mb-1.5">Service Category</label>
          <select value={form.service} onChange={updateField("service")} className={INPUT_CLASS} data-testid="inquiry-service-select">
            <option value="">Select service of interest…</option>
            {SERVICES.map((service) => <option key={service.slug} value={service.title}>{service.title}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#2A0E20] mb-1.5">Manpower Requirement Details *</label>
          <textarea required rows={4} value={form.message} onChange={updateField("message")} placeholder="Job roles, headcount required, target deployment timeline, and destination country…" className={INPUT_CLASS} data-testid="inquiry-message-input" />
        </div>

        <div className="pt-1">
          <label className="flex items-start gap-2.5 cursor-pointer text-xs text-[#2A0E20]/80">
            <input
              type="checkbox"
              required
              checked={form.dpdpConsent || false}
              onChange={updateChecked("dpdpConsent")}
              className="mt-0.5 rounded border-[#811F5D]/30 text-[#811F5D] focus:ring-[#D4AF37] h-4 w-4 shrink-0 accent-[#811F5D]"
              data-testid="inquiry-dpdp-checkbox"
            />
            <span>
              I consent to Vardhamaan Consultancy Services processing this data in accordance with India&apos;s{" "}
              <Link to="/privacy" className="text-[#811F5D] hover:underline font-semibold" target="_blank" rel="noopener noreferrer">
                DPDP Act 2023 & Privacy Policy
              </Link>
              .
            </span>
          </label>
        </div>

        <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
          <button
            type="submit"
            disabled={sending}
            className="btn-shine inline-flex items-center gap-2 bg-[#811F5D] text-white px-8 py-4 font-bold tracking-wide transition-all duration-300 hover:bg-[#4A1136] hover:gap-3 disabled:opacity-60 active:scale-[0.97] shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 rounded-xl text-sm"
            data-testid="inquiry-submit-button"
          >
            {sending ? <Loader2 size={18} className="animate-spin text-[#D4AF37]" /> : <Send size={17} className="text-[#D4AF37]" />}
            <span>{sending ? "Transmitting…" : "Send Inquiry"}</span>
          </button>
          <span className="flex items-center gap-1.5 text-xs text-[#2A0E20]/60">
            <ShieldCheck size={14} className="text-[#D4AF37]" /> Confidential & Secure
          </span>
        </div>
      </form>
    </FadeIn>
  );
};

const DirectContact = () => (
  <FadeIn delay={0.15}>
    <div className="bg-[#4A1136] rounded-3xl p-8 lg:p-10 relative overflow-hidden shadow-2xl border border-[#D4AF37]/20 h-full flex flex-col justify-between">
      <div className="absolute inset-0 hero-pattern opacity-30 pointer-events-none" />
      <div className="relative z-10">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#D4AF37] font-mono text-[11px] uppercase tracking-wider mb-4 border border-[#D4AF37]/30">
          <Clock size={12} /> Instant Connect
        </span>
        <h3 className="font-heading font-extrabold text-white text-2xl mb-2">Direct lines</h3>
        <p className="text-white/70 text-sm mb-7">Reach key regional mobilization coordinators immediately.</p>
        
        <div className="space-y-3.5">
          <a href="tel:+918446497833" className="flex items-center gap-3.5 text-white/90 hover:text-[#D4AF37] bg-white/5 hover:bg-white/10 p-3.5 rounded-xl border border-white/10 transition-all active:scale-[0.98]" data-testid="contact-phone-amol">
            <div className="w-9 h-9 rounded-lg bg-[#811F5D] flex items-center justify-center shrink-0 text-[#D4AF37]">
              <Phone size={17} />
            </div>
            <div>
              <p className="text-xs text-white/60">India (Operations)</p>
              <p className="text-sm font-semibold">Amol — +91 84464 97833</p>
            </div>
          </a>
          <a href="tel:+919828229443" className="flex items-center gap-3.5 text-white/90 hover:text-[#D4AF37] bg-white/5 hover:bg-white/10 p-3.5 rounded-xl border border-white/10 transition-all active:scale-[0.98]" data-testid="contact-phone-uttam">
            <div className="w-9 h-9 rounded-lg bg-[#811F5D] flex items-center justify-center shrink-0 text-[#D4AF37]">
              <Phone size={17} />
            </div>
            <div>
              <p className="text-xs text-white/60">India (Sourcing & Verification)</p>
              <p className="text-sm font-semibold">Uttam — +91 98282 29443</p>
            </div>
          </a>
          <a href="tel:+97455621384" className="flex items-center gap-3.5 text-white/90 hover:text-[#D4AF37] bg-white/5 hover:bg-white/10 p-3.5 rounded-xl border border-white/10 transition-all active:scale-[0.98]" data-testid="contact-phone-qatar">
            <div className="w-9 h-9 rounded-lg bg-[#811F5D] flex items-center justify-center shrink-0 text-[#D4AF37]">
              <Phone size={17} />
            </div>
            <div>
              <p className="text-xs text-white/60">Qatar / GCC Desk</p>
              <p className="text-sm font-semibold">Qatar Line — +974 5562 1384</p>
            </div>
          </a>
        </div>

        <div className="w-full h-px bg-white/15 my-7" />
        
        <p className="text-xs font-mono text-white/50 uppercase tracking-widest mb-3">Official Mailboxes</p>
        <div className="space-y-2.5">
          {EMAILS.map((email) => (
            <a key={email} href={`mailto:${email}`} className="flex items-center gap-3 text-white/80 hover:text-[#D4AF37] text-sm break-all transition-colors p-2 rounded-lg hover:bg-white/5" data-testid={`contact-email-${email.split("@")[0]}`}>
              <Mail size={16} className="shrink-0 text-[#D4AF37]" />
              <span>{email}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  </FadeIn>
);

export const ContactMain = () => (
  <section className="bg-[#FAF7F2] py-20 lg:py-28" data-testid="contact-main-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-14 items-start">
      <InquiryForm />
      <DirectContact />
    </div>
  </section>
);

export const OfficesSection = () => (
  <section className="bg-white border-t border-[#811F5D]/10 py-20 lg:py-28" data-testid="offices-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <FadeIn className="mb-14">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-3">Our Global Footprint</p>
        <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl lg:text-4xl tracking-tight">
          Four offices. Two countries.<br />One unified coordination desk.
        </h2>
      </FadeIn>
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {OFFICES.map((office) => (
          <StaggerItem key={office.city}>
            <div
              className="rounded-2xl border border-[#811F5D]/15 p-7 h-full bg-white transition-all duration-300 hover:border-[#811F5D] hover:shadow-xl hover:-translate-y-1.5 flex flex-col justify-between"
              data-testid={`office-card-${office.city.split(" ")[0].toLowerCase()}`}
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-[#FAF7F2] flex items-center justify-center mb-5 border border-[#D4AF37]/30">
                  <MapPin size={22} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-heading font-bold text-[#2A0E20] text-lg">{office.city}</h3>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#811F5D] font-bold mt-1 mb-4">{office.country}</p>
                <p className="text-sm text-[#6B5A64] leading-relaxed">{office.address}</p>
              </div>
              <div className="pt-5 mt-5 border-t border-[#811F5D]/10">
                <a href={`tel:${office.phone.replace(/[^0-9+]/g, '')}`} className="text-sm font-semibold text-[#811F5D] hover:text-[#4A1136] flex items-center gap-1.5">
                  <Phone size={14} className="text-[#D4AF37]" /> {office.phone}
                </a>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);